import type { TilesetType } from '@/common/data-upload';
import { axios } from '@/lib/utils/axios';

import { getResponse } from '../common';
import { API_URLS } from '../query.client';

export type TilesetCapabilities = {
	/**
	 * True when the backend drives the converter itself: the source goes straight
	 * to S3 and the conversion is queued from the key. This is the only upload
	 * path; the field is kept because the API still reports it.
	 */
	directUpload: boolean;
	/**
	 * False when converter orchestration is off, so a file upload has nowhere to
	 * go and the UI must not offer it. Absent on older backends, read as enabled.
	 */
	enabled?: boolean;
	maxUploadBytes: number;
	types: TilesetType[];
	acceptedFormats: Record<string, string[]>;
};

/**
 * Whether this backend accepts tileset uploads, and the limits it applies.
 *
 * Cached for the session: the answer only changes when the backend redeploys,
 * and every upload would otherwise pay for the round trip.
 */
let cached: Promise<TilesetCapabilities | null> | null = null;

export function getTilesetCapabilities(): Promise<TilesetCapabilities | null> {
	cached ??= axios
		.get(API_URLS.DATA_UPLOAD.TILESET_CAPABILITIES)
		.then((response) => getResponse<TilesetCapabilities>(response.data))
		.catch((error) => {
			// An older backend has no such endpoint; attempt the upload rather than
			// block it, and let the request itself report any real failure.
			console.warn('Tileset capabilities unavailable; attempting the upload anyway.', error);
			return null;
		});
	return cached;
}
